
  import { defineComponent, PropType } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import textMixin from '@/common/mixins/text';
  import { TApiResponse } from '@/common/types/response';

  import { ANALYTICS_NOTES_COMMON_GETTERS_NAME } from '../../store/analyticsNotesCommon/name';
  import {
    ANALYTICS_NOTES_YEARLY_ACTIONS_NAME,
    ANALYTICS_NOTES_YEARLY_GETTERS_NAME,
  } from '../../store/analyticsNotesYearly/name';
  import { TAnalyticsNotesFilter, TAnalyticsNotesForeignTradeTemplateData } from '../../types/analytics-notes';
  import {
    ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL,
    formatAnalyticsNotesDisplayNumber,
    formatAnalyticsNotesNumberOrUndefined,
    getAnalyticsNotesExportChangeVerb,
    getAnalyticsNotesTradeChangeVerb,
    isAnalyticsNotesUndefinedNumber,
    resolveAnalyticsNotesTrendOrUndefined,
    TAnalyticsNotesFormattableNumber,
  } from '../../utils/analytics-notes/foreignTradeText';
  import { getCostTypeName } from '../../utils/options';
  import AnalyticsNotesForeignTradeTextWrapper from './AnalyticsNotesForeignTradeTextWrapper.vue';

  export default defineComponent({
    name: 'AnalyticsNotesForeignTradeTextYearly',
    components: {
      AnalyticsNotesForeignTradeTextWrapper,
    },
    mixins: [textMixin],
    props: {
      filter: {
        type: Object as PropType<TAnalyticsNotesFilter>,
        required: true,
      },
    },
    data(): {
      isTemplateDataLoading: boolean;
      isInitLoadedData: boolean;
    } {
      return {
        isTemplateDataLoading: false,
        isInitLoadedData: false,
      };
    },
    computed: {
      ...mapGetters({
        getCountryDeclensions: ANALYTICS_NOTES_COMMON_GETTERS_NAME.getCountryDeclensions,
        getForeignTradeTemplateData: ANALYTICS_NOTES_YEARLY_GETTERS_NAME.getForeignTradeTemplateData,
      }),
      countryNameInstrumental() {
        return this.getCountryDeclensions?.countryInInstrumental;
      },
      getCostTypeName() {
        return getCostTypeName(this.filter.costType).toLowerCase();
      },
      getBalanceDescription(): string {
        return resolveAnalyticsNotesTrendOrUndefined(this.getForeignTradeTemplateData?.balanceValue, (balanceValue) =>
          balanceValue >= 0 ? 'Положительное' : 'Отрицательное',
        );
      },
      // TODO: Эти все методы надо переделать в стратегию.
      getTotalTradeSummaryText(): string {
        const totalTradeValue = this.getForeignTradeTemplateData?.totalTradeValue;
        const totalTradeDynamics = this.getForeignTradeTemplateData?.totalTradeDynamics;

        const isTotalTradeValueUndefined = isAnalyticsNotesUndefinedNumber(totalTradeValue);
        const isTotalTradeDynamicsUndefined = isAnalyticsNotesUndefinedNumber(totalTradeDynamics);

        if (isTotalTradeValueUndefined && isTotalTradeDynamicsUndefined) {
          return `составил ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isTotalTradeValueUndefined && isTotalTradeDynamicsUndefined) {
          return `составил ${this.formatCurrency(totalTradeValue)} ${
            this.getCostTypeName
          } и ${getAnalyticsNotesTradeChangeVerb(totalTradeValue)} по сравнению с ${this.formatDisplayNumber(
            this.getForeignTradeTemplateData?.previousPeriodYear,
          )} г. на ${this.formatPercentAbs(ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL)}%`;
        }

        return `составил ${this.formatCurrency(totalTradeValue)} ${this.getCostTypeName} и ${this.getChangeVerb(
          totalTradeDynamics,
        )} по сравнению с ${this.formatDisplayNumber(
          this.getForeignTradeTemplateData?.previousPeriodYear,
        )} г. на ${this.formatPercentAbs(totalTradeDynamics)}`;
      },
      getExportSummaryText(): string {
        const exportDynamics = this.getForeignTradeTemplateData?.exportDynamics;
        const exportValue = this.getForeignTradeTemplateData?.exportValue;

        const isExportDynamicsUndefined = isAnalyticsNotesUndefinedNumber(exportDynamics);
        const isExportValueUndefined = isAnalyticsNotesUndefinedNumber(exportValue);

        if (isExportDynamicsUndefined && isExportValueUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isExportValueUndefined && isExportDynamicsUndefined) {
          return `${getAnalyticsNotesTradeChangeVerb(
            exportValue,
          )} на ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}% (${this.formatCurrency(exportValue)} ${
            this.getCostTypeName
          })`;
        }

        return `${this.getChangeVerbExport(exportDynamics)} на ${this.formatPercentAbs(
          exportDynamics,
        )} (до уровня ${this.formatCurrency(exportValue)} ${this.getCostTypeName})`;
      },
      getImportSummaryText(): string {
        const importDynamics = this.getForeignTradeTemplateData?.importDynamics;
        const importValue = this.getForeignTradeTemplateData?.importValue;

        const isImportDynamicsUndefined = isAnalyticsNotesUndefinedNumber(importDynamics);
        const isImportValueUndefined = isAnalyticsNotesUndefinedNumber(importValue);

        if (isImportDynamicsUndefined && isImportValueUndefined) {
          return `${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isImportValueUndefined && isImportDynamicsUndefined) {
          return `
          ${getAnalyticsNotesTradeChangeVerb(
            importValue,
          )} на ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}% (${this.formatCurrency(importValue)} ${
            this.getCostTypeName
          })`;
        }

        return `${this.getChangeVerb(importDynamics)} на ${this.formatPercentAbs(
          importDynamics,
        )} (${this.formatCurrency(importValue)} ${this.getCostTypeName})`;
      },
      getWithoutTekTotalTradeSummaryText(): string {
        const withoutTekTotalTradeValue = this.getForeignTradeTemplateData?.withoutTekTotalTradeValue;
        const withoutTekTotalTradeDynamics = this.getForeignTradeTemplateData?.withoutTekTotalTradeDynamics;

        const isWithoutTekTotalTradeValueUndefined = isAnalyticsNotesUndefinedNumber(withoutTekTotalTradeValue);
        const isWithoutTekTotalTradeDynamicsUndefined = isAnalyticsNotesUndefinedNumber(withoutTekTotalTradeDynamics);

        if (isWithoutTekTotalTradeValueUndefined && isWithoutTekTotalTradeDynamicsUndefined) {
          return `по товарообороту ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isWithoutTekTotalTradeValueUndefined && isWithoutTekTotalTradeDynamicsUndefined) {
          return `по товарообороту ${this.formatCurrency(withoutTekTotalTradeValue)} ${
            this.getCostTypeName
          } (темп прироста ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL})`;
        }

        return `по товарообороту ${this.formatCurrency(withoutTekTotalTradeValue)} ${
          this.getCostTypeName
        } (${this.getChangeType(withoutTekTotalTradeDynamics)} на ${this.formatPercentAbs(
          withoutTekTotalTradeDynamics,
        )})`;
      },
      getWithoutTekExportSummaryText(): string {
        const withoutTekExportValue = this.getForeignTradeTemplateData?.withoutTekExportValue;
        const withoutTekExportDynamics = this.getForeignTradeTemplateData?.withoutTekExportDynamics;

        const isWithoutTekExportValueUndefined = isAnalyticsNotesUndefinedNumber(withoutTekExportValue);
        const isWithoutTekExportDynamicsUndefined = isAnalyticsNotesUndefinedNumber(withoutTekExportDynamics);

        if (isWithoutTekExportValueUndefined && isWithoutTekExportDynamicsUndefined) {
          return `по экспорту ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isWithoutTekExportValueUndefined && isWithoutTekExportDynamicsUndefined) {
          return `по экспорту ${this.formatCurrency(withoutTekExportValue)} ${
            this.getCostTypeName
          } (темп прироста ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL})`;
        }

        return `по экспорту ${this.formatCurrency(withoutTekExportValue)} ${this.getCostTypeName} (${this.getChangeType(
          withoutTekExportDynamics,
        )} на ${this.formatPercentAbs(withoutTekExportDynamics)})`;
      },
      getBalanceSummaryText(): string {
        const withoutTekBalanceValue = this.getForeignTradeTemplateData?.withoutTekBalanceValue;
        const balanceValue = this.getForeignTradeTemplateData?.balanceValue;

        const isWithoutTekBalanceValueUndefined = isAnalyticsNotesUndefinedNumber(withoutTekBalanceValue);
        const isBalanceValueUndefined = isAnalyticsNotesUndefinedNumber(balanceValue);

        if (isWithoutTekBalanceValueUndefined && isBalanceValueUndefined) {
          return `сальдо ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (!isWithoutTekBalanceValueUndefined && isBalanceValueUndefined) {
          return `сальдо ${this.formatCurrency(withoutTekBalanceValue, false)} ${
            this.getCostTypeName
          }; сальдо России в торговле с ${this.countryNameInstrumental} в ${this.formatDisplayNumber(
            this.getForeignTradeTemplateData?.endPeriodYear,
          )} году составило ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}`;
        }

        if (isWithoutTekBalanceValueUndefined && !isBalanceValueUndefined) {
          return `сальдо ${ANALYTICS_NOTES_UNDEFINED_NUMBER_LABEL}; сальдо России в торговле с ${
            this.countryNameInstrumental
          } в ${this.formatDisplayNumber(
            this.getForeignTradeTemplateData?.endPeriodYear,
          )} году составило ${this.formatCurrency(balanceValue, false)} ${this.getCostTypeName}`;
        }

        return `сальдо ${this.formatCurrency(withoutTekBalanceValue, false)} ${this.getCostTypeName} ${
          this.getBalanceDescription
        } сальдо России в торговле с ${this.countryNameInstrumental} в ${this.formatDisplayNumber(
          this.getForeignTradeTemplateData?.endPeriodYear,
        )} году составило ${this.formatCurrency(balanceValue, false)} ${this.getCostTypeName}`;
      },
    },
    methods: {
      ...mapActions({
        searchForeignTradeTemplateData: ANALYTICS_NOTES_YEARLY_ACTIONS_NAME.searchForeignTradeTemplateData,
      }),
      formatDisplayNumber: formatAnalyticsNotesDisplayNumber,
      formatPercentAbs(value: TAnalyticsNotesFormattableNumber): string {
        return formatAnalyticsNotesNumberOrUndefined(
          value,
          (numericValue) => `${this.formatNumber(Math.abs(numericValue), 1)}%`,
        );
      },
      getChangeVerb: getAnalyticsNotesTradeChangeVerb,
      getChangeVerbExport: getAnalyticsNotesExportChangeVerb,
      formatCurrency(value: TAnalyticsNotesFormattableNumber, useAbsoluteValue: boolean = true): string {
        return formatAnalyticsNotesNumberOrUndefined(value, (numericValue) => {
          const preparedValue = useAbsoluteValue ? Math.abs(numericValue) : numericValue;
          return this.formatNumber(preparedValue, 1);
        });
      },
      getChangeType(dynamics: TAnalyticsNotesFormattableNumber): string {
        return resolveAnalyticsNotesTrendOrUndefined(dynamics, (normalizedValue) =>
          normalizedValue >= 0 ? 'прирост' : 'снижение',
        );
      },
      async loadTemplateData(filter: TAnalyticsNotesFilter) {
        this.isTemplateDataLoading = true;

        try {
          const response = (await this.searchForeignTradeTemplateData(
            filter,
          )) as TApiResponse<TAnalyticsNotesForeignTradeTemplateData>;

          if (response?.success) {
            this.isInitLoadedData = true;
          }
        } finally {
          this.isTemplateDataLoading = false;
        }
      },
    },
    watch: {
      filter: {
        handler(newVal: TAnalyticsNotesFilter) {
          this.loadTemplateData(newVal);
        },
        deep: true,
        immediate: true,
      },
    },
  });
